import { useRef, useState } from 'react';
import { api, ApiError } from '../api/client';

type Props = {
  recipeId: string;
  coverImage: string | null | undefined;
  onUpdated: (newCover: string | null) => void;
};

export default function PhotoUploader({ recipeId, coverImage, onUpdated }: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    setError(null);
    try {
      const form = new FormData();
      form.append('cover', file);
      const res = await api.upload<{ coverImage: string; url: string }>(
        `/api/v1/recipes/${recipeId}/cover`,
        form,
      );
      onUpdated(res.coverImage);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'upload fallito');
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  }

  async function onRemove() {
    if (!coverImage) return;
    setBusy(true);
    setError(null);
    try {
      await api.delete(`/api/v1/recipes/${recipeId}/cover`);
      onUpdated(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'rimozione fallita');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-3">
      <div className="aspect-[4/3] w-full rounded-2xl overflow-hidden bg-surface relative">
        {coverImage ? (
          <img
            src={`/media/${coverImage}`}
            alt=""
            className="h-full w-full object-cover"
            loading="lazy"
          />
        ) : (
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={busy}
            className="absolute inset-0 flex items-center justify-center flex-col gap-2 text-muted hover:text-brand"
          >
            <svg className="h-8 w-8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="5" width="18" height="14" rx="2" />
              <circle cx="9" cy="11" r="2" />
              <path d="m21 17-5-5-8 8" />
            </svg>
            <span className="text-sm font-medium">{busy ? 'caricamento…' : 'Aggiungi foto'}</span>
          </button>
        )}
      </div>
      {coverImage && (
        <div className="flex gap-2">
          <button
            type="button"
            className="btn-ghost flex-1"
            onClick={() => inputRef.current?.click()}
            disabled={busy}
          >
            {busy ? '…' : 'cambia foto'}
          </button>
          <button
            type="button"
            className="btn-ghost text-brand"
            onClick={onRemove}
            disabled={busy}
          >
            rimuovi
          </button>
        </div>
      )}
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
        className="hidden"
        onChange={onFileChange}
      />
      {error && <p className="text-brand text-sm font-medium">{error}</p>}
    </div>
  );
}
